import {
  AuthorizationUser,
  getData,
  IAuthorizationService,
  IDataAccessService,
} from '@price-for-profit/micro-services';
import { chunk } from 'lodash';
import { CLIENT_ID, DATABASE_LABEL } from 'shared/constants';
import { Commodity, CommoditySql, CommodityUserMapping, DatabaseLabel, UserMapping } from 'shared/types';
import { alphaNumericSort } from 'shared/utility';

const COMMODITY_TABLE = 'Commodity';
const COMMODITY_USER_MAPPING_TABLE = 'CommodityUserMapping';
const USER_MAPPING_TABLE = 'UserMapping';

export interface CreateUserMappingProps {
  userMapping: UserMapping;
  commodities: CommodityUserMapping[];
}

export interface UpdateUserMappingProps
  extends Pick<UserMapping, 'id' | 'updatedDate' | 'updatedBy' | 'updatedByEmail'> {
  createCommodities: CommodityUserMapping[];
  deleteCommodities: CommodityUserMapping[];
}

export interface IUserMappingService {
  getUserMappings(): Promise<UserMapping[]>;
  getUserMapping(userId: string): Promise<UserMapping>;
  createUserMapping(props: CreateUserMappingProps): Promise<void>;
  updateUserMapping(props: UpdateUserMappingProps): Promise<void>;
  deleteUserMapping(userMapping: UserMapping): Promise<void>;
  getUsers(): Promise<AuthorizationUser[]>;
  getCommodities(): Promise<Commodity[]>;
  getAssignedCommodities(mappingId: string): Promise<CommodityUserMapping[]>;
}

export class UserMappingService implements IUserMappingService {
  constructor(private dataAccessService: IDataAccessService, private authorizationService: IAuthorizationService) {}

  async getCommodities(): Promise<Commodity[]> {
    const data = await getData(page =>
      this.dataAccessService.getCollection<CommoditySql, DatabaseLabel>({
        clientId: CLIENT_ID,
        databaseLabel: DATABASE_LABEL,
        tableId: COMMODITY_TABLE,
        pageSize: 100,
        page,
      })
    );
    return alphaNumericSort(
      data.map<Commodity>(({ name, id }) => ({ id, name })),
      lookup => lookup.name
    );
  }

  async getAssignedCommodities(mappingId: string): Promise<CommodityUserMapping[]> {
    const data = await getData(page =>
      this.dataAccessService.getCollection<CommodityUserMapping, DatabaseLabel>({
        clientId: CLIENT_ID,
        databaseLabel: DATABASE_LABEL,
        tableId: COMMODITY_USER_MAPPING_TABLE,
        collectionFilter: {
          property: 'userMappingId',
          operator: 'eq',
          value: mappingId,
        },
        pageSize: 100,
        page,
      })
    );
    return data;
  }

  async getUsers(): Promise<AuthorizationUser[]> {
    const users = await this.authorizationService.getClientUsersRestricted({ clientId: CLIENT_ID });
    return alphaNumericSort(users, u => u.displayName);
  }

  async getUserMappings(): Promise<UserMapping[]> {
    const data = await getData(page =>
      this.dataAccessService.getCollection<UserMapping, DatabaseLabel>({
        clientId: CLIENT_ID,
        databaseLabel: DATABASE_LABEL,
        tableId: USER_MAPPING_TABLE,
        page,
        pageSize: 100,
      })
    );

    return data;
  }

  async getUserMapping(userId: string): Promise<UserMapping> {
    const { data } = await this.dataAccessService.getSingle<UserMapping, DatabaseLabel>({
      clientId: CLIENT_ID,
      databaseLabel: DATABASE_LABEL,
      tableId: USER_MAPPING_TABLE,
      key: userId,
    });

    return data;
  }

  async createUserMapping({ userMapping, commodities }: CreateUserMappingProps): Promise<void> {
    await this.dataAccessService.addRow<UserMapping, DatabaseLabel>({
      clientId: CLIENT_ID,
      tableId: USER_MAPPING_TABLE,
      databaseLabel: DATABASE_LABEL,
      payload: userMapping,
    });

    await this.addCommoditiesUserMapping(commodities);
  }

  async updateUserMapping({
    createCommodities,
    deleteCommodities,
    updatedDate,
    id,
    updatedBy,
    updatedByEmail,
  }: UpdateUserMappingProps): Promise<void> {
    await this.dataAccessService.patchRow<UserMapping, DatabaseLabel>({
      clientId: CLIENT_ID,
      tableId: USER_MAPPING_TABLE,
      databaseLabel: DATABASE_LABEL,
      key: id,
      operations: [
        { path: '/updatedDate', op: 'replace', value: updatedDate },
        { path: '/updatedBy', op: 'replace', value: updatedBy },
        { path: '/updatedByEmail', op: 'replace', value: updatedByEmail },
      ],
    });
    await this.deleteCommoditiesUserMapping(deleteCommodities);
    await this.addCommoditiesUserMapping(createCommodities);
  }

  async deleteUserMapping(userMapping: UserMapping): Promise<void> {
    await this.dataAccessService.deleteRow<UserMapping, DatabaseLabel>({
      clientId: CLIENT_ID,
      tableId: USER_MAPPING_TABLE,
      databaseLabel: DATABASE_LABEL,
      key: userMapping.id,
    });
  }

  async deleteCommoditiesUserMapping(commoditiesUserMapping: CommodityUserMapping[]): Promise<void> {
    for (const batch of chunk(commoditiesUserMapping, 10)) {
      await Promise.all(
        batch.map(commodity =>
          this.dataAccessService.deleteRow<CommodityUserMapping, DatabaseLabel>({
            clientId: CLIENT_ID,
            tableId: COMMODITY_USER_MAPPING_TABLE,
            databaseLabel: DATABASE_LABEL,
            key: commodity.id,
          })
        )
      );
    }
  }

  async addCommoditiesUserMapping(commoditiesUserMapping: CommodityUserMapping[]): Promise<void> {
    for (const batch of chunk(commoditiesUserMapping, 10)) {
      await Promise.all(
        batch.map(commodity =>
          this.dataAccessService.addRow<CommodityUserMapping, DatabaseLabel>({
            clientId: CLIENT_ID,
            tableId: COMMODITY_USER_MAPPING_TABLE,
            databaseLabel: DATABASE_LABEL,
            payload: commodity,
          })
        )
      );
    }
  }
}
